import { usePageTitle } from "@src/hooks/usePageTitle";
import React, { ReactElement, useCallback, useState } from "react";
import styled from "styled-components/macro";

const StyledRoot = styled.main`
  display: flex;
  height: 100vh;
  overflow: hidden;
  background-color: var(--page-background);

  @media print {
    display: block;
    height: 100%;
    overflow: visible;
  }
`;

const StyledSidebar = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: var(--sidebar-width);
  overflow: auto;
  background-color: var(--secondary-black);

  @media only screen and (max-width: 75rem) {
    width: var(--sidebar-width);
    position: fixed;
    height: 100%;
  }

  @media only screen and (max-width: 40rem) {
    flex-basis: 0;
    position: relative;
  }

  @media only print {
    display: none;
  }
`;

type StyledContentProps = Omit<PageProps, "title"> & {
  headerHeight?: number;
};
const StyledContent = styled.div<StyledContentProps>`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  max-width: calc(100vw - var(--sidebar-width));

  @media screen and (max-width: 75rem) {
    margin-left: var(--sidebar-width);
  }

  @media screen and (max-width: 40rem) {
    max-width: 100%;
    margin-left: 0;
  }

  @media print {
    max-width: 100%;
    margin-left: 0;
  }

  .main {
    flex-grow: 1;
    flex-shrink: 0;
    padding: ${(props) => (props.padMain ? "0 1rem" : null)};
    max-height: ${(props) =>
      props.headerHeight !== undefined
        ? `calc(100vh - ${props.headerHeight}px)`
        : "calc(100vh - var(--header-height))"};
  }

  @media print {
    max-height: auto;
  }
`;

type PageProps = {
  title: string;
  sidebar?: JSX.Element;
  main?: JSX.Element;
  header?:
    | JSX.Element
    | ((measureRef: (node: HTMLDivElement) => void) => JSX.Element);
  padMain?: boolean;
  children?: ReactElement;
  fullHeight?: boolean;
  ref?: React.RefObject<HTMLDivElement>;
};

export const PageLayout: React.FC<PageProps> = ({
  children,
  sidebar,
  main,
  header,
  padMain,
  title,
}) => {
  usePageTitle(title);
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const headerRefCallBack = useCallback((node: HTMLDivElement) => {
    if (node) {
      setHeaderHeight(node.clientHeight);
    }
  }, []);

  return (
    <StyledRoot>
      <div id="menu-portal-target" style={{ zIndex: 100000 }}></div>
      <StyledSidebar>{sidebar}</StyledSidebar>
      <StyledContent padMain={padMain} headerHeight={headerHeight}>
        <>
          {typeof header === "function" ? header(headerRefCallBack) : header}
          <div className="main">{main}</div>
          {children}
        </>
      </StyledContent>
    </StyledRoot>
  );
};

export const PageContentWithRightSidebar = styled.div<{ gap?: string }>`
  display: grid;
  grid-template-columns: 1fr min-content;
  grid-template-rows: 1fr;
  min-height: 100%;
  gap: ${(props) => (props.gap ? props.gap : "0")};

  @media only screen and (max-width: 75rem) {
    overflow-y: auto;
    display: flex;
    height: 100%;
    flex-flow: column;
  }
`;

export const PageRoot = styled.div<{
  noGap?: boolean;
  fullHeight?: boolean;
  noOverflow?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.noGap ? "0" : "1rem")};
  overflow-y: ${(props) => (props.noOverflow ? "visible" : "auto")};
  padding: 0 2rem;
  ${({ fullHeight }) => fullHeight && "height: 100vh"};
  ${({ fullHeight }) =>
    !fullHeight && "height: calc(100vh - var(--header-height))"};

  @media only screen and (max-width: 75rem) {
    overflow: auto;
  }

  @media only screen and (max-width: 40rem) {
    margin-top: var(--header-height);
  }

  @media print {
    height: 100%;
    overflow-y: visible;
  }
`;

export const PageRootWithBottomPad = styled(PageRoot)`
  padding-bottom: 4rem;
`;

export const PageRootWithNoPad = styled(PageRoot)`
  padding: 0;
`;

export const PageRootWithOnlyBottomPad = styled(PageRoot)`
  padding: 0 0 4rem 0;
`;

export const PageRootWithPadding = styled(PageRoot)`
  padding: 1.5rem;
`;
